/* eslint no-extend-native: ["error", { "exceptions": ["Array", "Object"] }] */

/**
 * Performs a grouping function on a set of elements and returns a dictionary of the result
 *
 * @param arr the set of objects to be grouped
 * @param selector a function defining how the objects will be grouped into keys
 */
export function groupBy<T> (arr: T[], keySelector: (arg: T) => string | number): { [key: string]: T[] } {
	if (arr == null) {
		return arr;
	}

	if (keySelector == null) {
		throw Error('keySelector must be provided.');
	}

	return arr.reduce((accumulator: { [key: string]: T[] }, value) => {
		(accumulator[keySelector(value)] = accumulator[keySelector(value)] || []).push(value);
		return accumulator;
	}, {});
}

/**
 * Transforms an object into an array of { key, value } pairs
 *
 * @param obj the object to be transformed
 */
export function keyMap<T> (obj: { [key: string]: T}): Array<{ key: string, value: T }> {
	return Object.keys(obj)
		.map((key) => ({ key, value: obj[key] }));
}

declare global {
	export interface Array<T> {
		/**
		 * Performs a grouping function on a set of elements and returns a dictionary of the result
		 */
		groupBy(selector: (arg: T) => string | number): {
			[key: string]: T[];
		};
	}

	export interface Object {
		/**
		 * Transforms an object into an array of { key, value }
		 */
		keyMap<T>(): Array<{ key: string, value: T }>;
	}
}

export default function install (): void {
	Object.defineProperty(Array.prototype, 'groupBy', {
		value (selector: (arg: unknown) => string | number) {
			return groupBy(this, selector);
		},
		configurable: true
	});

	Object.defineProperty(Object.prototype, 'keyMap', {
		value () {
			return keyMap(this);
		},
		configurable: true
	});
}
